import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import {
  GetCmpUserGuideDocument,
  SearchCmpUserGuideBlocksDocument,
  SearchCmpUserGuideBlocksQuery,
  SearchCmpUserGuideBlocksQueryVariables,
  useGetCmpUserGuidePageQuery,
  useMarkCmpUserGuideViewedMutation,
} from "../../../graphql/types";
import { RoutePaths } from "../../../enumStrings";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../../utils/apolloErrorPolicy";
import { buildStaffErrorFallback, getUserFacingErrorText } from "../../../utils/userFacingError";
import { applyGuideHit, attachContentsSearch, GuideHighlight, storeGuideHit, takeGuideHit } from "./guideSearchHighlight";
import { addGuideFrameFooter, GUIDE_FOOTER_STYLES } from "./guideFrameFooter";

// Chamber Member Pro™ User Guide (Monday 13280799093, Claude Design CMP
// Dashboard (19) "Guide - *.dc.html"). The guide text is served by the backend
// (getCmpUserGuidePage); this bundle only holds the frame and its styling.
// Search (Monday 13300893078) lives inside Guide Contents, in the design's own
// search box, and opens pages at the matching section (guideSearchHighlight.ts).

export const CMP_USER_GUIDE_CONTENTS_SLUG = "contents";

export const getGuideSlugFromSearch = (search: string): string => {
  const slug = new URLSearchParams(search).get("page") || CMP_USER_GUIDE_CONTENTS_SLUG;
  return /^[a-z0-9-]{1,80}$/.test(slug) ? slug : CMP_USER_GUIDE_CONTENTS_SLUG;
};

export const guideHref = (slug: string): string =>
  slug === CMP_USER_GUIDE_CONTENTS_SLUG ? RoutePaths.chamberAdmin_guide : `${RoutePaths.chamberAdmin_guide}?page=${slug}`;

// Guide errors stay on this screen (never the dashboard-wide error page).
// Unexpected failures still reach Sentry (wave 1, Oct 19 2026).
export const guideOperationContext = CALLER_HANDLED_USER_ERROR_CONTEXT;

// Design sheet (doc-page.js): #f5f5f4 desk with 48px 24px padding, 8.5in
// white page with a 0.75in margin on screen and on every printed page
// (NAV-G10, NAV-G13).
export const GUIDE_DESK_COLOR = "#f5f5f4";
const FRAME_STYLES = `
html,body{margin:0;background:${GUIDE_DESK_COLOR};}
body{font-family:'DM Sans',sans-serif;color:#0A0A0A;}
a{color:#0A0A0A;text-decoration:underline;}
a:hover{color:#B7791F;}
[data-guide-search]:focus{border-color:#E1A731 !important;}
[data-guide-page]{width:8.5in;max-width:calc(100% - 48px);box-sizing:border-box;margin:48px auto;padding:0.75in;background:#fff;border-radius:7px;box-shadow:0 2px 10px rgba(20,20,19,.12);}
@media (max-width:720px){[data-guide-page]{padding:24px 18px;margin:16px auto 32px;max-width:calc(100% - 32px);}}
@page{size:letter;margin:0.75in;}
@media print{html,body{background:#fff;}[data-guide-toolbar],[data-guide-search-wrap]{display:none !important}[data-guide-page]{width:auto;max-width:none;margin:0;padding:0;box-shadow:none;border-radius:0;}}
${GUIDE_FOOTER_STYLES}`;

export const buildGuideFrameDocument = (html: string): string =>
  `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">` +
  `<link rel="preconnect" href="https://fonts.googleapis.com"><link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">` +
  `<style>${FRAME_STYLES}</style></head><body>${html}</body></html>`;

/** What a click inside the guide should do. */
export const getGuideClickAction = (
  target: { closest: (selector: string) => Element | null } | null,
): { kind: "guide"; slug: string } | { kind: "print" } | { kind: "external"; href: string } | null => {
  if (!target) return null;
  if (target.closest('[data-guide-act="print"]')) return { kind: "print" };
  const link = target.closest("a[href]");
  const href = link?.getAttribute("href") || "";
  const guide = /^#guide=([a-z0-9-]+)$/.exec(href);
  if (guide) return { kind: "guide", slug: guide[1] };
  if (/^https?:\/\//i.test(href) || /^mailto:/i.test(href)) return { kind: "external", href };
  return null;
};

/**
 * Clears this user's "New" / "Updated" pill. Called when the user chooses the
 * guide (sidebar, a guide link or a search result), not on the automatic
 * landing, so the pill stays visible until the user opens the guide.
 */
export const useMarkCmpUserGuideViewed = () => {
  const [markViewed] = useMarkCmpUserGuideViewedMutation({
    refetchQueries: [{ query: GetCmpUserGuideDocument, context: guideOperationContext }],
    context: guideOperationContext,
  });
  const sentRef = useRef(false);
  return useCallback(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    Promise.resolve(markViewed()).catch(() => {
      sentRef.current = false;
    });
  }, [markViewed]);
};

const CmpUserGuide: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const client = useApolloClient();
  const slug = getGuideSlugFromSearch(location.search);
  const { data, loading, error, refetch } = useGetCmpUserGuidePageQuery({ variables: { slug }, fetchPolicy: "cache-and-network", context: guideOperationContext });
  const markViewed = useMarkCmpUserGuideViewed();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const searchCleanupRef = useRef<(() => void) | null>(null);
  const highlightRef = useRef<GuideHighlight | null>(null);
  const html = data?.getCmpUserGuidePage.html;
  const srcDoc = useMemo(() => (html ? buildGuideFrameDocument(html) : ""), [html]);

  useEffect(() => () => {
    searchCleanupRef.current?.();
    searchCleanupRef.current = null;
  }, [slug]);

  const searchGuide = useCallback(async (query: string) => {
    const result = await client.query<SearchCmpUserGuideBlocksQuery, SearchCmpUserGuideBlocksQueryVariables>({
      query: SearchCmpUserGuideBlocksDocument,
      variables: { query },
      fetchPolicy: "cache-first",
      context: guideOperationContext,
    });
    return result.data.searchCmpUserGuideBlocks;
  }, [client]);

  const onFrameLoad = useCallback(() => {
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    if (!frame || !doc) return;
    addGuideFrameFooter(doc, data?.getCmpUserGuidePage.title || "");
    doc.addEventListener("click", (event) => {
      const action = getGuideClickAction(event.target as Element | null);
      if (!action) return;
      event.preventDefault();
      if (action.kind === "print") frame.contentWindow?.print();
      if (action.kind === "guide") {
        markViewed();
        navigate(guideHref(action.slug));
      }
      if (action.kind === "external") window.open(action.href, "_blank", "noopener,noreferrer");
    });
    frame.contentWindow?.scrollTo(0, 0);
    searchCleanupRef.current?.();
    searchCleanupRef.current = attachContentsSearch(doc, {
      search: searchGuide,
      open: (hit) => {
        storeGuideHit({ ...hit, t: Date.now() });
        markViewed();
        navigate(guideHref(hit.slug));
      },
    });
    const hit = takeGuideHit(slug);
    highlightRef.current = hit ? applyGuideHit(doc, hit) : null;
  }, [data?.getCmpUserGuidePage.title, markViewed, navigate, searchGuide, slug]);

  return (
    // NAV-G13: on desktop the guide sits flush under the page top (the
    // layout's 16px top padding is cancelled); on phones the menu button
    // space stays.
    <div className="flex h-full min-h-[100vh] flex-col md:-mt-4" style={{ backgroundColor: GUIDE_DESK_COLOR }} data-testid="cmp-user-guide">
      {error && !html && (
        <div className="m-[24px] rounded-[8px] border border-red-200 bg-red-50 p-[16px] text-[14px] text-red-900" role="alert">
          {getUserFacingErrorText(error, buildStaffErrorFallback("load this guide page"))}{" "}
          <button className="font-semibold underline" onClick={() => void refetch()} type="button">Try again</button>
        </div>
      )}
      {loading && !html && <p className="m-[24px] text-[14px] text-[#727272]">Loading the user guide…</p>}
      {html && (
        <iframe
          key={slug}
          ref={frameRef}
          className="block w-full flex-1 border-0"
          onLoad={onFrameLoad}
          // No scripts run inside the guide; same origin lets the dashboard handle links and Print.
          sandbox="allow-same-origin allow-modals"
          srcDoc={srcDoc}
          style={{ minHeight: "calc(100vh - 96px)" }}
          title={data?.getCmpUserGuidePage.title || "Chamber Member Pro™ Guide"}
        />
      )}
    </div>
  );
};

export default CmpUserGuide;
